import { makeStyles, tokens, Caption1 } from '@fluentui/react-components'
import { EditableField } from '../../components/EditableField'
import type { Demand } from '../../types/demand'
import { bayerColors } from '../../theme'

const useStyles = makeStyles({
  section: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    padding: tokens.spacingVerticalL,
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: bayerColors.navy,
    margin: 0,
  },
  caption: {
    display: 'block',
    margin: `0 0 ${tokens.spacingVerticalM}`,
  },
  kvRow: {
    display: 'grid',
    gridTemplateColumns: '200px 1fr',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke3,
    ':last-child': {
      borderBottomStyle: 'none',
    },
  },
  kvKey: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
  },
  kvValue: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    lineHeight: 1.5,
  },
})

export interface BusinessCaseTabProps {
  demand: Demand
  canEdit: boolean
  onSave: (path: string, value: string | string[] | number) => void
}

interface Row {
  /** Path the dispatcher uses to PATCH. Stable across schema migrations. */
  path: string
  label: string
  placeholder: string
  /** Function to read the current value from the Demand record. */
  read: (d: Demand) => string
}

/**
 * Demand detail Business Case tab — mirrors mockup §7169-7220.
 *
 * Seven multiline EditableFields. `dependencies`, `risks`, and
 * `similar_tools_details` are now first-class TEXT columns on the v2
 * substrate (#707); the round-trip writes to PATCH /demands and reads
 * back the same field on the next refresh.
 */
export function BusinessCaseTab({ demand, canEdit, onSave }: BusinessCaseTabProps) {
  const styles = useStyles()

  const rows: Row[] = [
    {
      path: 'issue_problem',
      label: 'Issue / problem',
      placeholder: "What's the problem being solved?",
      read: (d) => d.issue_problem ?? '',
    },
    {
      path: 'purpose',
      // `purpose` is not yet on the schema — surfaces under `description` so
      // requestors see the same prose they entered at intake.
      label: 'Purpose',
      placeholder: 'What does success unlock?',
      read: (d) => d.description ?? '',
    },
    {
      path: 'target_user',
      label: 'Target user',
      placeholder: 'Who benefits?',
      read: (d) => d.target_user ?? '',
    },
    {
      path: 'background',
      label: 'Background',
      placeholder: 'Relevant context, history, prior work…',
      read: (d) => d.background ?? '',
    },
    {
      path: 'dependencies',
      label: 'Dependencies',
      placeholder: 'What must be in place first?',
      read: (d) => d.dependencies ?? '',
    },
    {
      path: 'risks',
      label: 'Risks',
      placeholder: 'What could go wrong?',
      read: (d) => d.risks ?? '',
    },
    {
      path: 'similar_tools_details',
      label: 'Similar tools considered',
      placeholder: 'Internal or external alternatives evaluated',
      read: (d) => d.similar_tools_details ?? '',
    },
  ]

  return (
    <section className={styles.section} data-testid="business-case-tab">
      <h3 className={styles.title}>Business case</h3>
      <Caption1 className={styles.caption}>
        Structured intake fields from the requestor.
        {canEdit ? ' Click any value to edit.' : ''}
      </Caption1>
      {rows.map((r) => (
        <div key={r.path} className={styles.kvRow} data-testid={`bcase-${r.path}`}>
          <span className={styles.kvKey}>{r.label}</span>
          <div className={styles.kvValue}>
            <EditableField
              value={r.read(demand)}
              path={r.path}
              onSave={onSave}
              disabled={!canEdit}
              multiline
              placeholder={r.placeholder}
              ariaLabel={r.label}
            />
          </div>
        </div>
      ))}
    </section>
  )
}
